import type React from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { isEmpty, isNil } from 'lodash-es';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { oneClickActionRequest } from '../../../redux/ducks/UserDucks';
import { OneClickActionTypeEnum } from '../../../redux/ducks/UserDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import MainLayout from '../../layout/MainLayout/MainLayout';
import Spinner from '../../layout/Spinner/Spinner';

interface OneClickActionProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const OneClickAction: React.FC<OneClickActionProps> = ({ ssrFetchedData }) => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [onCall, setOnCall] = useState<boolean>(false);
  const [onSuccess, setOnSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const { oneClickActionResponse } = useSelector(
    (state: RootReducerType) => state.user,
  );
  const { appConfig } = useSelector((state: RootReducerType) => state.config);

  useEffect(
    () => () => {
      setOnCall(false);
      setOnSuccess(false);
    },
    [],
  );

  useEffect(() => {
    const codeParam = searchParams.get('code');
    const typeParam = searchParams.get('type');

    if (!onCall && !isEmpty(codeParam) && !isEmpty(typeParam)) {
      setOnCall(true);
      dispatch(
        oneClickActionRequest({
          type: OneClickActionTypeEnum.Email,
          code: codeParam || '',
        }),
      );
    }
  }, [onCall]);

  useEffect(() => {
    if (!isNil(oneClickActionResponse)) {
      if (oneClickActionResponse?.data) {
        setOnSuccess(true);
      } else {
        toast.error(t('static.onclickpage.unsubscribe_email_failure_message'));
      }
    }
  }, [oneClickActionResponse]);

  return (
    <MainLayout
      customSize={2000}
      categories={false}
      search={false}
      navbar
      footer
      ssrFetchedData={ssrFetchedData}
    >
      <div className="flex min-w-full grow flex-col items-center justify-center">
        {onSuccess ? (
          <div className="flex grow flex-col items-center justify-center text-slate-500">
            <div className="text-2xl font-bold">
              {t('static.onclickpage.unsubscribe_success_message_title')}
            </div>
            <div className="text-lg font-semibold">
              {t('static.onclickpage.unsubscribe_success_message_description', {
                name: !isNil(appConfig) ? appConfig.service.name : '',
              })}
            </div>
          </div>
        ) : (
          <Spinner color="fill-sky-600" />
        )}
      </div>
    </MainLayout>
  );
};

export default OneClickAction;
